import { EVENT_ID, type DeferredCredit, type FeePayoutRecord, type Session, type Ticket } from "./types";
import type { FieldMutation } from "./db";
import type { CashCountRecord } from "./cash";
import { orderMutationsForApply } from "./field-mutation-order";

export interface FieldApiConfig {
  endpoint: string;
  token?: string;
}

export type FieldPushResult = {
  mutationId: string;
  outcome: string;
  error?: string;
  reason?: string;
  already?: boolean;
};

/** Bootstrap／pull 應秒級完成；push 一批現場 mutation 可能到一分鐘。 */
const FIELD_PULL_TIMEOUT_MS = 12_000;
const FIELD_PUSH_TIMEOUT_MS = 60_000;
/** Cloudflare Pages 約 50 次 subrequest／請求；10 筆核票略過 snapshot 後仍低於上限。 */
export const FIELD_PUSH_CHUNK_SIZE = 10;

export const FIELD_TIMEOUT_MESSAGE = "連接逾時，請重試";

function fieldNetworkError(error: unknown): Error {
  const name = error instanceof Error ? error.name : "";
  const message = error instanceof Error ? error.message : String(error);
  if (name === "TimeoutError" || name === "AbortError" || /abort|timeout/i.test(message)) {
    return new Error(FIELD_TIMEOUT_MESSAGE);
  }
  if (error instanceof TypeError) {
    return new Error("無法連線場日 API：請確認開發伺服器有在跑（/api/field）");
  }
  return error instanceof Error ? error : new Error(String(error));
}

async function postField<T>(
  config: FieldApiConfig,
  body: unknown,
  timeoutMs = FIELD_PULL_TIMEOUT_MS,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json;charset=utf-8",
  };
  if (config.token) headers["x-field-token"] = config.token;

  let response: Response;
  try {
    response = await fetch(config.endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(timeoutMs),
    });
  } catch (error) {
    throw fieldNetworkError(error);
  }

  let data: (T & { ok?: boolean; error?: string }) | null = null;
  try {
    data = (await response.json()) as T & { ok?: boolean; error?: string };
  } catch {
    /* non-JSON body */
  }
  if (!response.ok) {
    throw new Error(data?.error || `Field API ${response.status}`);
  }
  return (data ?? ({} as T)) as T;
}

export type FieldSnapshotResponse = {
  ok: boolean;
  error?: string;
  tickets: Ticket[];
  deferredCredits?: DeferredCredit[];
  feePayouts: FeePayoutRecord[];
  cashCounts: CashCountRecord[];
  performerOptions: Record<Session, string[]>;
  event?: { ssot?: "sheet" | "db" };
  cursor: string;
  ops?: unknown[];
  results?: FieldPushResult[];
};

async function readNdjson(response: Response, onEvent: (event: Record<string, unknown>) => void | Promise<void>) {
  if (!response.body) {
    const text = await response.text();
    for (const line of text.split("\n")) {
      const trimmed = line.trim();
      if (trimmed) await onEvent(JSON.parse(trimmed) as Record<string, unknown>);
    }
    return;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newline = buffer.indexOf("\n");
    while (newline >= 0) {
      const line = buffer.slice(0, newline).trim();
      buffer = buffer.slice(newline + 1);
      if (line) await onEvent(JSON.parse(line) as Record<string, unknown>);
      newline = buffer.indexOf("\n");
    }
  }
  const tail = buffer.trim();
  if (tail) await onEvent(JSON.parse(tail) as Record<string, unknown>);
}

function emptySnapshot(): FieldSnapshotResponse {
  return {
    ok: true,
    tickets: [],
    feePayouts: [],
    cashCounts: [],
    performerOptions: { afternoon: [], evening: [] },
    cursor: "",
    results: [],
  };
}

export async function fieldBootstrap(config: FieldApiConfig, since?: string) {
  return postField<FieldSnapshotResponse>(
    config,
    {
      action: since ? "pull" : "bootstrap",
      eventId: EVENT_ID,
      since,
    },
    FIELD_PULL_TIMEOUT_MS,
  );
}

export async function fieldPush(
  config: FieldApiConfig,
  mutations: FieldMutation[],
  options: {
    onResult?: (item: FieldPushResult) => void | Promise<void>;
    includeSnapshot?: boolean;
  } = {},
) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json;charset=utf-8",
  };
  if (config.token) headers["x-field-token"] = config.token;

  let response: Response;
  try {
    response = await fetch(config.endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: "push",
        eventId: EVENT_ID,
        mutations,
        includeSnapshot: options.includeSnapshot !== false,
      }),
      signal: AbortSignal.timeout(FIELD_PUSH_TIMEOUT_MS),
    });
  } catch (error) {
    throw fieldNetworkError(error);
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("ndjson")) {
    let data: FieldSnapshotResponse | null = null;
    try {
      data = (await response.json()) as FieldSnapshotResponse;
    } catch {
      /* non-JSON body */
    }
    if (!response.ok) {
      throw new Error(data?.error || `Field API ${response.status}`);
    }
    const payload = data ?? emptySnapshot();
    for (const item of payload.results || []) {
      await options.onResult?.(item);
    }
    return payload;
  }

  if (!response.ok) {
    throw new Error(`Field API ${response.status}`);
  }

  const snapshot = emptySnapshot();
  try {
    await readNdjson(response, async (event) => {
      const type = String(event.type || "");
      if (type === "result") {
        const item = {
          mutationId: String(event.mutationId || ""),
          outcome: String(event.outcome || ""),
          error: typeof event.error === "string" ? event.error : undefined,
          reason: typeof event.reason === "string" ? event.reason : undefined,
          already: Boolean(event.already),
        };
        snapshot.results = [...(snapshot.results || []), item];
        await options.onResult?.(item);
        return;
      }
      if (type === "snapshot") {
        snapshot.ok = true;
        snapshot.tickets = (event.tickets as Ticket[]) || [];
        snapshot.deferredCredits = event.deferredCredits as DeferredCredit[] | undefined;
        snapshot.feePayouts = (event.feePayouts as FeePayoutRecord[]) || [];
        snapshot.cashCounts = (event.cashCounts as CashCountRecord[]) || [];
        snapshot.performerOptions =
          (event.performerOptions as Record<Session, string[]>) || snapshot.performerOptions;
        snapshot.event = event.event as FieldSnapshotResponse["event"];
        snapshot.cursor = String(event.cursor || "");
        return;
      }
      if (type === "error") {
        throw new Error(String(event.error || "上傳失敗"));
      }
    });
  } catch (error) {
    throw fieldNetworkError(error);
  }
  return snapshot;
}

export async function fieldSetDeviceRole(
  config: FieldApiConfig,
  deviceId: string,
  role: string,
  label?: string,
) {
  return postField<{ ok: boolean }>(config, {
    action: "setDeviceRole",
    eventId: EVENT_ID,
    deviceId,
    role,
    label,
  });
}

export async function fieldReport(config: FieldApiConfig) {
  return postField<{
    ok: boolean;
    tickets: Array<Record<string, unknown>>;
    feePayouts: Array<Record<string, unknown>>;
    cashCounts: Array<Record<string, unknown>>;
    generatedAt: string;
  }>(config, { action: "report", eventId: EVENT_ID });
}

/** Checkpoint sync: push outbox in chunks, then pull authoritative snapshot. */
export async function runCheckpointSync(
  config: FieldApiConfig,
  outbox: FieldMutation[],
  options: { onResult?: (item: FieldPushResult) => void | Promise<void> } = {},
) {
  let remaining = orderMutationsForApply(outbox);
  while (remaining.length) {
    const chunk = remaining.slice(0, FIELD_PUSH_CHUNK_SIZE);
    const finished = new Set<string>();
    const pushed = await fieldPush(config, chunk, {
      includeSnapshot: false,
      onResult: async (item) => {
        await options.onResult?.(item);
        if (item.mutationId) finished.add(item.mutationId);
      },
    });
    if (!pushed.ok) throw new Error(pushed.error || "上傳失敗");
    remaining = remaining.filter((mutation) => !finished.has(mutation.mutationId));
    if (finished.size === 0) throw new Error(pushed.error || "上傳失敗");
  }
  const pulled = await fieldBootstrap(config);
  if (!pulled.ok) throw new Error(pulled.error || "下載失敗");
  return pulled;
}
